import * as Types from '@trello/graphql/generated';

import { useQuickLoad } from '@trello/quickload';
import type { TrelloQueryHookOptions, TrelloLazyQueryHookOptions, TrelloSuspenseQueryHookOptions } from '@trello/quickload';
import DocumentNode from '@trello/graphql/documentNode';
import * as Apollo from '@apollo/client';
export const PluginDeleteDocument = {"kind":"Document","definitions":[{"kind":"OperationDefinition","operation":"query","name":{"kind":"Name","value":"PluginDelete"},"variableDefinitions":[{"kind":"VariableDefinition","variable":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}},"type":{"kind":"NonNullType","type":{"kind":"NamedType","name":{"kind":"Name","value":"ID"}}}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"plugin"},"arguments":[{"kind":"Argument","name":{"kind":"Name","value":"id"},"value":{"kind":"Variable","name":{"kind":"Name","value":"idPlugin"}}}],"directives":[{"kind":"Directive","name":{"kind":"Name","value":"client"}}],"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"listings"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"id"}},{"kind":"Field","name":{"kind":"Name","value":"locale"}},{"kind":"Field","name":{"kind":"Name","value":"name"}}]}},{"kind":"Field","name":{"kind":"Name","value":"name"}},{"kind":"Field","name":{"kind":"Name","value":"public"}},{"kind":"Field","name":{"kind":"Name","value":"usageBrackets"},"selectionSet":{"kind":"SelectionSet","selections":[{"kind":"Field","name":{"kind":"Name","value":"boards"}}]}}]}}]}}]} as unknown as DocumentNode;
const defaultOptions = {"context":{"operationName":"PluginDelete","document":PluginDeleteDocument}} as const;
export type PluginDeleteQueryVariables = Types.Exact<{
  idPlugin: Types.Scalars['ID']['input'];
}>;


export type PluginDeleteQuery = (
  { __typename: 'Query' }
  & { plugin?: Types.Maybe<(
    { __typename: 'Plugin' }
    & Pick<Types.Plugin, 'id' | 'name' | 'public'>
    & { listings?: Types.Maybe<Array<(
      { __typename: 'Plugin_Listings' }
      & Pick<Types.Plugin_Listings, 'id' | 'locale' | 'name'>
    )>>, usageBrackets: (
      { __typename: 'Plugin_UsageBrackets' }
      & Pick<Types.Plugin_UsageBrackets, 'boards'>
    ) }
  )> }
);

/**
 * __usePluginDeleteQuery__
 *
 * To run a query within a React component, call `usePluginDeleteQuery` and pass it any options that fit your needs.
 * When your component renders, `usePluginDeleteQuery` returns an object from Apollo Client that contains loading, error, and data properties
 * you can use to render your UI.
 *
 * @param baseOptions options that will be passed into the query, supported options are listed on: https://www.apollographql.com/docs/react/api/react-hooks/#options;
 *
 * @example
 * const { data, loading, error } = usePluginDeleteQuery({
 *   variables: {
 *      idPlugin: // value for 'idPlugin'
 *   },
 * });
 */
export function usePluginDeleteQuery(
  baseOptions: TrelloQueryHookOptions<
    PluginDeleteQuery,
    PluginDeleteQueryVariables
  > &
    (
      | { variables: PluginDeleteQueryVariables; skip?: boolean }
      | { skip: boolean }
    ),
) {
  const isQuickLoading = useQuickLoad({
    waitOn: baseOptions?.waitOn || ['None'],
    document: PluginDeleteDocument,
    skip: baseOptions?.skip,
  });
  const options = {
    ...defaultOptions,
    ...baseOptions,
    skip: isQuickLoading || baseOptions?.skip,
  };
  const result = Apollo.useQuery<PluginDeleteQuery, PluginDeleteQueryVariables>(
    PluginDeleteDocument,
    options,
  );
  // reconstruct result because modifying the useQuery result actually changes apollo behavior because of memoization
  const trelloResult = { ...result };
  if (isQuickLoading) {
    trelloResult.loading = true;
  }

  return trelloResult;
}
export function usePluginDeleteLazyQuery(
  baseOptions?: TrelloLazyQueryHookOptions<
    PluginDeleteQuery,
    PluginDeleteQueryVariables
  >,
) {
  const options = { ...defaultOptions, ...baseOptions };
  return Apollo.useLazyQuery<PluginDeleteQuery, PluginDeleteQueryVariables>(
    PluginDeleteDocument,
    options,
  );
}
export function usePluginDeleteSuspenseQuery(
  baseOptions?:
    | Apollo.SkipToken
    | TrelloSuspenseQueryHookOptions<
        PluginDeleteQuery,
        PluginDeleteQueryVariables
      >,
) {
  const options =
    baseOptions === Apollo.skipToken
      ? baseOptions
      : { ...defaultOptions, ...baseOptions };
  return Apollo.useSuspenseQuery<PluginDeleteQuery, PluginDeleteQueryVariables>(
    PluginDeleteDocument,
    options,
  );
}
export type PluginDeleteQueryHookResult = ReturnType<
  typeof usePluginDeleteQuery
>;
export type PluginDeleteLazyQueryHookResult = ReturnType<
  typeof usePluginDeleteLazyQuery
>;
export type PluginDeleteSuspenseQueryHookResult = ReturnType<
  typeof usePluginDeleteSuspenseQuery
>;
export type PluginDeleteQueryResult = Apollo.QueryResult<
  PluginDeleteQuery,
  PluginDeleteQueryVariables
>;
